import React from 'react';
import { Link } from 'react-router-dom';
import Hero from '../components/Hero';
import DownloadSection from '../components/DownloadSection';
import Problem from '../components/Problem';
import Services from '../components/Services';
import Apps from '../components/Apps';
import WhatsNew from '../components/WhatsNew';
import Vision from '../components/Vision';
import Team from '../components/Team';
import Promo from '../components/Promo';
import Investment from '../components/Investment';
import Policies from '../components/Policies';
import SEO, { SITE_URL } from '../components/SEO';

const TITLE = "BikesDrop: Ghana's Delivery, Food, Groceries & POS App";
const DESCRIPTION =
  'Order from 1,000+ restaurants and stores, send a package, move cargo or ride an Okada. Earn as a rider or merchant, or book delivery for your own business as a vendor, with built in Point of Sale, on BikesDrop Business.';

const JSON_LD = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'Organization',
      '@id': `${SITE_URL}/#organization`,
      name: 'BikesDrop',
      legalName: 'BikesDrop Business and Logistics LTD',
      alternateName: ['BikesFood', 'BikesGroceries', 'BikesDrop Business'],
      url: SITE_URL,
      logo: `${SITE_URL}/assets/logo.jpg`,
      image: `${SITE_URL}/assets/og-image.jpg`,
      slogan: 'Your Ride, Your Delivery, Your Way.',
      description:
        "BikesDrop is Ghana's all-in-one delivery, food, groceries and logistics platform. Customers order everything through the BikesDrop app, while riders, truck drivers and merchants earn, and vendors book delivery for a business they already run, through BikesDrop Business, complete with a built in Point of Sale system for in person sales.",
      email: 'info@bikesdrop.com',
      telephone: '+233508571134',
      address: { '@type': 'PostalAddress', addressLocality: 'Accra', addressCountry: 'GH' },
      areaServed: { '@type': 'Country', name: 'Ghana' },
      founder: { '@type': 'Person', name: 'Isaac Tetteh', jobTitle: 'Founder, CEO & Developer' },
      contactPoint: [
        { '@type': 'ContactPoint', contactType: 'customer support', email: 'support@bikesdrop.com', areaServed: 'GH', availableLanguage: 'English' },
        { '@type': 'ContactPoint', contactType: 'general inquiries', email: 'info@bikesdrop.com', areaServed: 'GH', availableLanguage: 'English' },
      ],
      hasOfferCatalog: {
        '@type': 'OfferCatalog',
        name: 'BikesDrop Services',
        itemListElement: [
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Pickup & Delivery', description: 'Send a document, a parcel or a gift across town with live rider tracking.' } },
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'BikesFood', description: 'Order meals from over 1,000 local restaurants and food joints, Ghanaian and continental.' } },
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'BikesGroceries', description: 'Shop groceries from over 1,000 stores and local markets, from fresh produce to frozen foods.' } },
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Okada', description: 'Hop on the back of a verified rider’s motorbike and beat the traffic across town.' } },
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Cargo Delivery', description: 'Book a real truck with trained handlers for moving furniture, appliances and bulk loads.' } },
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Campus & Community Errands', description: 'Grocery runs, gas cylinder refills, printing and everyday campus and community tasks.' } },
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Schedule Delivery', description: 'Plan ahead and choose the exact date and time slot for a delivery.' } },
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Point of Sale for Merchants & Vendors', description: 'Built in Point of Sale so merchants and vendors can ring up in person sales at their own counter, separate from any online orders or deliveries.' } },
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Delivery for Vendors', description: 'Fixed rate Bike Delivery, negotiated Cargo Delivery or distance priced Pickup Delivery for a business that already has its own customers.' } },
        ],
      },
    },
    {
      '@type': 'WebSite',
      '@id': `${SITE_URL}/#website`,
      url: SITE_URL,
      name: 'BikesDrop',
      publisher: { '@id': `${SITE_URL}/#organization` },
      inLanguage: 'en',
    },
    {
      '@type': 'SoftwareApplication',
      name: 'BikesDrop',
      applicationCategory: 'ShoppingApplication',
      operatingSystem: 'Android, iOS',
      description:
        'Order food from 1,000+ restaurants through BikesFood, groceries from 1,000+ stores through BikesGroceries, send deliveries, book an Okada ride, move cargo and request campus or community errands, all with live tracking.',
      publisher: { '@id': `${SITE_URL}/#organization` },
    },
    {
      '@type': 'SoftwareApplication',
      name: 'BikesDrop Business',
      applicationCategory: 'BusinessApplication',
      operatingSystem: 'Android, iOS',
      description:
        'One business app for riders and truck drivers who earn by moving Ghana, merchants who list a shop or restaurant for new customers to find, and vendors who already have their own customers and just need delivery done right, with a built in Point of Sale for all of them.',
      publisher: { '@id': `${SITE_URL}/#organization` },
    },
  ],
};

export default function HomePage() {
  return (
    <>
      <SEO title={TITLE} description={DESCRIPTION} path="/" imageAlt="BikesDrop, Your Ride, Your Delivery, Your Way" jsonLd={JSON_LD} />
      <Hero />
      <DownloadSection />
      <Problem />
      <Services />
      <Apps />
      <WhatsNew />
      <Vision />
      <Team />
      <Promo />
      <Investment />
      <Policies />
    </>
  );
}

// Exported so Footer/Navbar can build a same-page anchor Link consistently:
// <Link to={homeAnchor('services')}>. From another route it's a real
// navigation to "/#services" (ScrollManager then scrolls once it lands);
// from Home itself react-router just updates the hash in place.
export function homeAnchor(id) {
  return `/#${id}`;
}
